/** One person in this workspace — the page behind a row of People (Batch 5).
 *
 * ## It is the Admin's page, and it finds the person the way the roster does
 *
 * There is no "get one member" endpoint for the roster facts. The page reads the Admin-only roster
 * (`useMembers`) and finds its row by id, so `$userId` authorizes nothing: a non-Admin is told whose
 * decision people are, and an id that is not on this workspace's roster says one sentence for
 * "mistyped" and "not a member here" alike — telling them apart would disclose that a person exists
 * elsewhere.
 *
 * ## What is shown
 *
 * Name, address, role (with what the role lets somebody do), status, access window and filings come
 * from the same `MemberOut` the roster renders. Three more facts come from one Admin-only read,
 * `GET /members/{id}/standing` (`useMemberStanding`), which exists because none of them is on the
 * roster and each is the Admin's to know and nobody else's:
 *
 * - **Second factor.** The policy (workspace-wide, and the two roles that are always required) *and*
 *   whether this person has an authenticator app or a passkey. A Contributor never asks: the server
 *   refuses it, and the page is not reachable to one.
 * - **Invited and joined.** The invitation this workspace issued to them, if one was redeemed, and
 *   the day the membership was created. A person the operator added directly has no invitation and
 *   the page says that instead of inventing a date.
 * - **Decisions.** Exact counts from the append-only decision table: approved, rejected,
 *   adjustment requested, and requests they submitted. A count of rows in this workspace, never a
 *   sum, and never read from the ledger (which holds identifiers and acts, not a roster of them).
 *
 * If that read fails the page says so beside the facts it would have given; the roster facts still
 * show. A failed read is never rendered as "no second factor" or "no decisions".
 *
 * ## Also counted: work that carries an owner
 *
 * Tasks, risks and risk actions, and the security events this person caused, from the first page of
 * the audit history (and the page says it is one page).
 *
 * ## Controls: only what can be reused as it is
 *
 * The engagement-period form and the auditor's reach are the roster's own components, rendered here
 * unchanged. Role and suspension are controls the roster owns inline and are not duplicated: a second
 * path to the same write is how two screens come to disagree, and the page links back to them.
 * Erasure is not offered: a company Admin cannot anonymise a person's account globally (ADR-0081).
 */

import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "@phosphor-icons/react";
import { useState } from "react";
import type { ReactNode } from "react";

import { useAuditHistory } from "../api/audit";
import { useMe } from "../api/auth";
import { useMfaPolicy } from "../api/mfaPolicy";
import { useMemberStanding, useMembers, useRisks } from "../api/queries";
import type { Member, MemberStanding } from "../api/queries";
import { useTasks } from "../api/work";
import { useReader } from "../api/profile";
import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import type { ReadState } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { instantDate, shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { EngagementPeriod } from "./PeopleEngagement";
import { EngagementReach } from "./PeopleReach";
import { CAN, LABEL, filingLabel, periodOf } from "./peopleRoles";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { BasisAndLimits, Facts } from "./ScreenParts";
import type { FactSpec } from "./ScreenParts";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";
import parts from "./ScreenParts.module.css";
import styles from "./Screen.module.css";
import rec from "./Record.module.css";
import own from "./PersonDetail.module.css";

const PERSON_TABS = [
  { key: "access", label: "Access" },
  { key: "work", label: "Work" },
  { key: "activity", label: "Activity" },
] as const;

function cls(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(" ");
}

function Pair({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className={rec.k}>{k}</dt>
      <dd className={rec.v}>{children}</dd>
    </>
  );
}

/** Status as a word and an icon as well as a tone (design rule 8). */
const STATUS: Record<string, { tone: StatusTone; word: string }> = {
  active: { tone: "ok", word: "Active" },
  suspended: { tone: "warn", word: "Workspace access suspended" },
  erased: { tone: "neutral", word: "Erased" },
};

function BackToPeople() {
  return (
    <Link className={styles.fixLink} to="/people" search={keepSearch} data-testid="person-back">
      <ArrowLeft size={13} weight="bold" aria-hidden="true" /> Back to People
    </Link>
  );
}

function NotOnTheRoster({ userId }: { userId: string }) {
  return (
    <Panel title="Person" column="1 / 13">
      <p className={styles.note} data-testid="person-not-found" data-user-id={userId}>
        Nobody with that address is a member of this workspace. It may have been mistyped, or belong
        to a person of another workspace.
      </p>
      <BackToPeople />
    </Panel>
  );
}

function AdminOnly() {
  return (
    <Panel title="Person" column="1 / 13">
      <p className={styles.note} data-testid="people-admin-only">
        An <strong>Admin</strong> administers people. Who may approve a filing is the most
        consequential setting in this workspace, and it is not a shared one.
      </p>
      <BackToPeople />
    </Panel>
  );
}

type Standing = ReadState<MemberStanding>;

/** The second factor: what the workspace requires, and whether this person has one. */
function SecondFactor({ member, standing }: { member: Member; standing: Standing }) {
  const policy = useMfaPolicy(true);
  const required = policy.data
    ? policy.data.require_mfa_for_all_members ||
      policy.data.mandatory_roles.some((role) => role === member.role)
    : undefined;
  return (
    <Panel title="Second factor" sub="What the workspace requires, and what this person has" column="1 / 13">
      <Loaded query={policy} of="The workspace's second-factor policy">
        {(read) => (
          <p className={own.factor} data-testid="person-mfa" data-required={required}>
            <StatusPill tone={required ? "info" : "neutral"}>
              {required ? "Required" : "Not required"}
            </StatusPill>
            <span>
              {required
                ? read.require_mfa_for_all_members
                  ? "This workspace requires a second factor of every member."
                  : `${LABEL[member.role] ?? member.role} is a role that needs a second factor before it can act.`
                : "This workspace does not require a second factor of this role."}
            </span>
          </p>
        )}
      </Loaded>
      <Loaded query={standing} of="This person's second-factor state">
        {(read) => (
          <>
            <p className={own.factor} data-testid="person-mfa-enrolled" data-enrolled={read.mfa_enrolled}>
              <StatusPill tone={read.mfa_enrolled ? "ok" : required ? "warn" : "neutral"}>
                {read.mfa_enrolled ? "Set up" : "Not set up"}
              </StatusPill>
              <span>
                Authenticator app: {read.authenticator_app ? "set up" : "not set up"}. Passkeys:{" "}
                {read.passkeys}.
              </span>
            </p>
            {required && !read.mfa_enrolled ? (
              <p className={styles.note} data-testid="person-mfa-missing">
                A second factor is required of this person and none is set up, so they are asked to
                set one up when they next sign in.
              </p>
            ) : null}
          </>
        )}
      </Loaded>
    </Panel>
  );
}

function AccessTab({
  member,
  lastAdmin,
  standing,
}: {
  member: Member;
  lastAdmin: boolean;
  standing: Standing;
}) {
  const reader = useReader();
  const engagementWindow = periodOf(member.period_scope);
  const [refusal, setRefusal] = useState<unknown>(null);
  const status = STATUS[member.status] ?? { tone: "neutral" as const, word: member.status };
  return (
    <div className={parts.stack}>
      <div className={parts.twoCol}>
        <div className={cls(own.main, parts.stack)}>
          <Panel title="Account" column="1 / 13">
            <dl className={cls(rec.kv, own.kv)} data-testid="person-facts">
              <Pair k="Name">
                {member.display_name}
                {member.is_you ? <span className={own.you}>you</span> : null}
              </Pair>
              <Pair k="Address">{member.email}</Pair>
              <Pair k="Role">
                <strong>{LABEL[member.role] ?? member.role}</strong>
                <span className={own.can}>{CAN[member.role] ?? member.role}</span>
              </Pair>
              <Pair k="Status">
                <StatusPill tone={status.tone}>{status.word}</StatusPill>
              </Pair>
              <Pair k="Access starts">
                {member.valid_from ? shortDate(member.valid_from) : "No start date is recorded"}
              </Pair>
              <Pair k="Access ends">
                {member.valid_to ? shortDate(member.valid_to) : "No end date"}
              </Pair>
              <Pair k="Invited">
                {standing.data ? (
                  standing.data.invited_at ? (
                    <span data-testid="person-invited">{instantDate(standing.data.invited_at, reader)}</span>
                  ) : (
                    <span data-testid="person-not-invited">
                      No invitation from this workspace is recorded for them
                    </span>
                  )
                ) : (
                  <span data-testid="person-invited-pending">
                    {standing.error ? "Could not be read" : "Reading…"}
                  </span>
                )}
              </Pair>
              <Pair k="Joined">
                {standing.data ? (
                  <span data-testid="person-joined">{instantDate(standing.data.joined_at, reader)}</span>
                ) : (
                  <span>{standing.error ? "Could not be read" : "Reading…"}</span>
                )}
              </Pair>
              <Pair k="Identifier">
                <span className={own.mono}>{member.user_id}</span>
              </Pair>
            </dl>
            {standing.error ? (
              <ReadFailed
                of="This person's invitation and joining dates"
                error={standing.error}
                onRetry={() => standing.refetch()}
              />
            ) : null}
            <p className={styles.note} data-testid="person-dates-basis">
              Invited is when the invitation this person accepted was issued. Joined is when their
              membership was created, which accepting it does. A person added directly by the
              operator has no invitation.
            </p>
          </Panel>

          {member.role === "auditor" ? (
            <Panel title="Engagement" sub="What this auditor may read" column="1 / 13">
              {engagementWindow ? (
                <p data-testid="person-engagement-window">
                  Data from {shortDate(engagementWindow.from)} to {shortDate(engagementWindow.to)}
                </p>
              ) : (
                <p className={own.warn} data-testid="auditor-unscoped">
                  No engagement period is recorded, so this auditor can read every year. An
                  engagement is for a year, not for ever.
                </p>
              )}
              {member.entity_scope === "whole_workspace" ? (
                <p className={own.warn} data-testid="auditor-whole-workspace">
                  No filing is named for this auditor, so they can read every company in the
                  workspace. An engagement is for a filing.
                </p>
              ) : null}
              {member.entity_scope === "named" ? (
                <ul className={own.filings} data-testid="auditor-filings">
                  {(member.engagement ?? []).map((filing) => (
                    <li key={filing.obligation_id}>Engaged for {filingLabel(filing)}</li>
                  ))}
                </ul>
              ) : null}
              <EngagementReach member={member} />
            </Panel>
          ) : null}
        </div>

        <div className={cls(own.side, parts.stack)}>
          <SecondFactor member={member} standing={standing} />
          <Panel title="Change access" column="1 / 13">
            <EngagementPeriod member={member} lastAdmin={lastAdmin} onFailed={setRefusal} />
            <WriteFailed
              error={refusal}
              of="this person's access period"
              onReload={() => setRefusal(null)}
            />
            <p className={styles.note}>
              Changing a role or suspending workspace access is done from the roster, where the
              last-Admin protections are explained. A company Admin cannot erase or globally
              anonymise a person&rsquo;s account.
            </p>
          </Panel>
        </div>
      </div>
    </div>
  );
}

const TASK_STATES = ["open", "in_progress", "blocked", "done", "cancelled"] as const;
const TASK_WORDS: Record<(typeof TASK_STATES)[number], string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

function WorkTab({ userId }: { userId: string }) {
  //: The queries, not `data = []`: a dropped request is not a person with nothing to do (EXP-29).
  const tasks = useTasks(false);
  const register = useRisks(true);
  return (
    <div className={parts.stack}>
      <Panel title="Tasks" sub="Owned by this person" column="1 / 13">
        <Loaded query={tasks} of="The work queue" empty="No task is recorded in this workspace.">
          {(all) => {
            const owned = all.filter((task) => task.owner_id === userId);
            const created = all.filter((task) => task.created_by === userId);
            return (
              <>
                <dl className={cls(rec.kv, own.kv)} data-testid="person-tasks">
                  {TASK_STATES.map((state) => (
                    <Pair key={state} k={TASK_WORDS[state]}>
                      {owned.filter((task) => task.state === state).length}
                    </Pair>
                  ))}
                  <Pair k="Created by them">{created.length}</Pair>
                </dl>
                {owned.length === 0 ? (
                  <p className={styles.note} data-testid="person-tasks-none">
                    No task is assigned to this person.
                  </p>
                ) : null}
                <p className={styles.note}>
                  Counted over every task this session can read. A task on a record outside an
                  engagement is left out of the queue for that reader.
                </p>
              </>
            );
          }}
        </Loaded>
      </Panel>

      <Panel title="Risks" sub="Owned by this person" column="1 / 13">
        <Loaded query={register} of="The risk register" empty="No risk is on the register.">
          {(rows) => {
            const mine = rows.filter((risk) => risk.owner_user_id === userId);
            const actions = rows.flatMap((risk) =>
              risk.actions
                .filter((action) => action.owner_user_id === userId)
                .map((action) => ({ risk, action })),
            );
            return (
              <>
                {mine.length === 0 ? (
                  <p className={styles.note} data-testid="person-risks-none">
                    This person owns no risk on the register.
                  </p>
                ) : (
                  <ul className={own.rows} data-testid="person-risks">
                    {mine.map((risk) => (
                      <li key={risk.id} className={own.rowItem}>
                        <Link
                          className={styles.fixLink}
                          to="/risks/$riskId"
                          params={{ riskId: risk.id }}
                          search={keepSearch}
                        >
                          {risk.title}
                        </Link>
                        <span className={own.rowMeta}>
                          {risk.state === "closed" ? "Closed" : "Open"}
                          {risk.review_overdue ? " · review overdue" : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className={own.sumLine} data-testid="person-risk-actions">
                  Owns {actions.filter(({ action }) => !action.completed).length} open response
                  action{actions.filter(({ action }) => !action.completed).length === 1 ? "" : "s"}{" "}
                  and {actions.filter(({ action }) => action.completed).length} completed.
                </p>
              </>
            );
          }}
        </Loaded>
      </Panel>
    </div>
  );
}

function ActivityTab({ userId, standing }: { userId: string; standing: Standing }) {
  const history = useAuditHistory(true, 0);
  const reader = useReader();
  return (
    <div className={parts.stack}>
      <Panel title="Security events" sub="Caused by this person" column="1 / 13">
        {history.error ? (
          <ReadFailed
            of="The security history"
            error={history.error}
            onRetry={() => void history.refetch()}
          />
        ) : history.isPending ? (
          <Empty busy>Reading the security history…</Empty>
        ) : (
          (() => {
            const theirs = history.data.events.filter((event) => event.actor_id === userId);
            return (
              <>
                {theirs.length === 0 ? (
                  <p className={styles.note} data-testid="person-events-none">
                    None of the {history.data.events.length} most recent security events was caused
                    by this person.
                  </p>
                ) : (
                  <ul className={own.rows} data-testid="person-events">
                    {theirs.slice(0, 10).map((event) => (
                      <li key={event.id} className={own.rowItem}>
                        <span className={own.mono}>{event.event_type}</span>
                        <span className={own.rowMeta}>{instantDate(event.created_at, reader)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className={styles.note} data-testid="person-events-limit">
                  {history.data.has_more
                    ? `This is the newest ${history.data.events.length} events only; older ones are in the full history.`
                    : `This is the whole history: ${history.data.events.length} events.`}{" "}
                  <Link className={styles.fixLink} to="/audit" search={keepSearch}>
                    Open the security history ›
                  </Link>
                </p>
              </>
            );
          })()
        )}
      </Panel>
      <Panel title="Decisions and submissions" sub="Counted from the approval record of this workspace" column="1 / 13">
        <Loaded query={standing} of="This person's decision counts">
          {(read) => (
            <>
              <dl className={cls(rec.kv, own.kv)} data-testid="person-decisions">
                <Pair k="Approved">{read.decisions.approved}</Pair>
                <Pair k="Rejected">{read.decisions.rejected}</Pair>
                <Pair k="Asked for an adjustment">{read.decisions.adjustment_requested}</Pair>
                <Pair k="Requests they submitted">{read.decisions.submitted}</Pair>
              </dl>
              {read.decisions.approved +
                read.decisions.rejected +
                read.decisions.adjustment_requested +
                read.decisions.submitted ===
              0 ? (
                <p className={styles.note} data-testid="person-decisions-none">
                  This person has not decided or submitted any approval in this workspace.
                </p>
              ) : null}
            </>
          )}
        </Loaded>
        <p className={styles.note} data-testid="person-decisions-basis">
          Each count is of decisions recorded in this workspace, by outcome, and a person who
          approved twice at two stages of one request counts twice. They are not read from the audit
          ledger, which names a person by identifier only. Why a reviewer decided is not shown here.
        </p>
        <p className={styles.note}>
          <Link className={styles.fixLink} to="/ledger" search={keepSearch}>
            Open the audit ledger ›
          </Link>
        </p>
      </Panel>
    </div>
  );
}

function PersonBody({ member, roster }: { member: Member; roster: readonly Member[] }) {
  const [tab, selectTab] = useUrlTab(PERSON_TABS);
  //: Mounted once for the page: the Access and Activity tabs read the same answer, and PersonBody
  //: only renders for an Admin, which is the only role the server answers.
  const standing = useMemberStanding(member.user_id, true);
  const status = STATUS[member.status] ?? { tone: "neutral" as const, word: member.status };
  const activeAdmins = roster.filter((m) => m.role === "admin" && m.status === "active").length;
  const lastAdmin = member.role === "admin" && member.status === "active" && activeAdmins <= 1;
  const facts: FactSpec[] = [
    { key: "person-role", label: "Role", value: LABEL[member.role] ?? member.role, textual: true, note: CAN[member.role] },
    { key: "person-status", label: "Status", value: status.word, textual: true, note: member.status === "active" ? "Can sign in to this workspace" : "Cannot act in this workspace" },
    {
      key: "person-access-ends",
      label: "Access ends",
      value: member.valid_to ? shortDate(member.valid_to) : "No end date",
      textual: true,
      note: member.valid_from ? `Started ${shortDate(member.valid_from)}` : undefined,
    },
  ];
  return (
    <>
      <PageHeader
        subtitle={`${member.email} · ${LABEL[member.role] ?? member.role}`}
        tabs={
          <RecordTabs
            tabs={PERSON_TABS}
            tab={tab}
            onSelect={selectTab}
            label="Person"
            saying={`Showing ${PERSON_TABS.find((entry) => entry.key === tab)?.label ?? "Access"} for ${member.display_name}`}
          />
        }
        actions={<BackToPeople />}
      />
      <Facts facts={facts} label="This person at a glance" columns={3} />
      <div className={parts.area}>
        <RecordPanel tab={tab}>
          {tab === "work" ? (
            <WorkTab userId={member.user_id} />
          ) : tab === "activity" ? (
            <ActivityTab userId={member.user_id} standing={standing} />
          ) : (
            <AccessTab member={member} lastAdmin={lastAdmin} standing={standing} />
          )}
        </RecordPanel>
      </div>
      <div className={parts.area}>
        <BasisAndLimits testId="person-basis">
          <p>
            A person appears elsewhere as an identifier and never as a name in the audit ledger, so
            that record can be both unalterable and erasable. Names are shown here, to an Admin, from
            the same roster the People screen reads.
          </p>
        </BasisAndLimits>
      </div>
    </>
  );
}

function PersonScreenBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const { userId } = useParams({ from: "/people/$userId" });
  const isAdmin = me?.role === "admin";
  //: The query, so a failed roster is not "that person is not a member" (EXP-29).
  const roster = useMembers(isAdmin);
  const member = roster.data?.find((entry) => entry.user_id === userId);

  return (
    <ScreenFrame
      screen={screen}
      title={member?.display_name ?? "Person"}
      crumb="one person, as the Admin's roster holds them"
      bodyScrolls
    >
      {me && !isAdmin ? (
        <AdminOnly />
      ) : (
        <Loaded
          query={roster}
          of="The people in this workspace"
          empty={<NotOnTheRoster userId={userId} />}
        >
          {(rows) => {
            const found = rows.find((entry) => entry.user_id === userId);
            return found ? <PersonBody member={found} roster={rows} /> : <NotOnTheRoster userId={userId} />;
          }}
        </Loaded>
      )}
    </ScreenFrame>
  );
}

export function PersonScreen() {
  return (
    <RequireSession>
      <PersonScreenBody />
    </RequireSession>
  );
}
